import { api, createFormData, convertFileToBlob } from '@/utils/api';
import { sendMessageToActiveServiceWorkers, sendMessageToAllServiceWorkers } from '@/utils/service_workers';

import _ from 'lodash';

const MODULE_VERSION = 2; //UPDATE VERSION WITH EVERY BREAKING CHANGE OR ATTRIBUTE REMOVAL

const getDefaultState = () => {
  return {
    version: undefined, //TODO Add Loading animations while api is queried
    user: null
  }
};

const messageChannel = new MessageChannel();

//Initialize communication port once service workers have become active
//Send the communication port to the active service workers - only if they control this website - return message indicates that caching is active
sendMessageToActiveServiceWorkers({type: 'AUTH_TOKEN_PORT_INITIALIZATION'}, [messageChannel.port2], true);


//Listen to messages and respond with the auth token if requested through this port only (Secure channel)
messageChannel.port1.onmessage = (event) => {
  if (event.data && event.data.action === 'getAuthToken') {
    messageChannel.port1.postMessage({
      type: 'AUTH_TOKEN',
      authToken: api.getToken().value
    });
  }
  if (event.data && event.data.action === 'initializedAuthenticationRelay') { //TODO Test, that it is inactive on native platforms!
    api.setAuthenticationRelayActive(true); //Only set to true if this page instance is controlled by a service worker with active caching
  }
};

export default {
  namespaced: true,

  state: getDefaultState,
  getters: {
    getToken () {
      return api.getToken().value;
    },
    isAuthenticationRelayActive () {
      return api.getAuthenticationRelayActive().value;
    },
    isCachingActive () {
      return api.isCachingActive(); //Does not get notified on change!
    },
    getName (state) {
      if (state.user) {
        return state.user.name;
      } else {
        return null;
      }
    },
    getBirthname (state) {
      if (state.user) {
        return state.user.birthname;
      } else {
        return null;
      }
    },
    getEmail (state) {
      if (state.user) {
        return state.user.email;
      } else {
        return null;
      }
    },
    getUser (state) {
      return state.user;
    },
    getRoles () {
      return [
        'veterinarian',
        'veterinarian_student',
        'authenticated'
      ]
    },
    isUserPresent (state) {
      return (
        api.getToken().value != null && 
        state.user != null 
      )
    },
    getRequestedRole (state) {
      if (state.user != null) return state.user.requested_role;
    },
    getConfirmedLegalVersions (state) {
      if (state.user != null) return state.user.legal_documents;
    },
    isRequestedRoleSet (state) {
      return (
        state.user != null &&
        state.user.requested_role != null
      )
    },
    //Inverse of the above but user is authenticated
    requestedRoleNeeded (state) {
      return (
        api.getToken().value != null && 
        state.user != null &&
        state.user.requested_role == null
      )
    },
    isInRequestedRole (state) {
      return (
        state.user != null &&
        state.user.requested_role === state.user.role
      )
    },
    hasRoleProofProvided (state) {
      return (
        state.user != null &&
        state.user.role_proof_provided === true
      )
    },
    hasRoleBeenReviewed (state) {
      return (
        state.user != null &&
        state.user.role_request_reviewed == true
      )
    },
    isAuthenticated (state) {
      return (
        api.getToken().value != null && 
        state.user != null &&
        state.user.confirmed === true &&
        state.user.blocked !== true &&
        state.user.force_password_reset_on_next_login !== true &&
        state.user.requested_role != null //User can't progress from Login procedure unless completed setup
      )
    },
    isConfirmed (state) {
      return (state.user != null && state.user.confirmed === true);
    },
    isBlocked (state) {
      return (state.user != null && state.user.blocked === true);
    },
    passwordResetEnforced (state) {
      return (state.user != null && state.user.force_password_reset_on_next_login === true);
    },
    unlockUserNeeded (state) {
      return (
        api.getToken().value && 
        state.user &&
        (
        state.user.confirmed !== true ||
        state.user.blocked === true ||
        state.user.force_password_reset_on_next_login === true
        )
      )
    },
    hasPermission (state) {
      return function(permission) {
        if (state.user != null && state.user.additional_permissions != null) {
          for (let userPermission of state.user.additional_permissions) {
            if (userPermission != null && userPermission == permission) {
              return true;
            }
          }
        }
        return false;
      }
    },
    isVeterinarian (state) {
      return (state.user != null && state.user.role != null && (
        state.user.role.includes('veterinarian')
      ))
    },
    roleRequiresProof () {
      return (role) => {
        return (role !== 'authenticated');
      }
    },
    requestedRoleRequiresProof (state, getters) {
      return (state.user != null && getters['roleRequiresProof'](state.user.requested_role));
    }
  },
  mutations: {
    setNewToken (state, newToken) {
      api.setToken(newToken);
    },
    setNewUser (state, newUser) {
      state.user = newUser;
    },
    removeTokenAndUser (state) {
      api.removeToken();
      state.user = null;
    },
    checkVersion (state) { //Checks the version of this module and sets the state to default, if version is different
      //Used to reset state on breaking changes and to remove attributes that are no longer needed
      if (state.version !== MODULE_VERSION) {
        let defaultState = getDefaultState();
        Object.assign(state, defaultState); //Use setters to not impact any reactive functionality
        //Search for unused attributes and set them undefined, to remove them from the persisted state
        for (let key of Object.keys(state)){
          if (!(key in defaultState)){
            state[key] = undefined;
          }
        }
        state.version = MODULE_VERSION;
      }
    },
    clearPersonalData (state) {
      let defaultState = {};
      Object.assign(defaultState, getDefaultState());
      api.removeToken();
      //Clear all personal data in the caches, once the service worker is available
      sendMessageToAllServiceWorkers({type: 'CLEAR_PERSONAL_DATA'});
      state.user = defaultState.user;
    }
  },
  actions: {
    login (context, { user, password }) { //TODO DELETE USER CREDENTIALS IMEDIATELY
      return new Promise((resolve, reject) => {
        api
        .post('/auth/local', {
          identifier: user,
          password: password
        }, {skipQueue: true}) //Skip upload queue
        .then(response => response.completionPromise)
        .then(response => {
          context.commit('setNewToken', response.data.jwt);
          context.dispatch('fetchUser').catch((error) => reject(error));
          resolve();
        })
        .catch(error => {
          reject(error);
        });
      });
    },
    register (context, { name, birthname, user, password, newsletter, legal_versions }) { //TODO DELETE USER CREDENTIALS IMEDIATELY
      return new Promise((resolve, reject) => {
        api
        .post('/auth/local/register', {
          name: name,
          birthname: birthname,
          email: user,
          password: password,
          newsletter: newsletter,
          legal_versions: legal_versions
        }, {skipQueue: true}) //Skip upload queue
        .then(response => response.completionPromise)
        .then(response => {
          context.commit('setNewToken', response.data.jwt);
          return context.dispatch('fetchUser');
        })
        .then(() => {
          resolve();
        })
        .catch(error => {
          reject(error);
        });
      });
    },
    forgotPassword (context, user) { //TODO DELETE USER CREDENTIALS IMEDIATELY
      return new Promise((resolve, reject) => {
        api
        .post('/auth/forgot-password', {
          email: user
        }, {skipQueue: true}) //Skip upload queue
        .then(response => response.completionPromise)
        .then(() => {
          resolve();
        })
        .catch(error => {
          reject(error);
        });
      });
    },
    resetPassword (context, { code, password, passwordConfirmation }) { //TODO DELETE USER CREDENTIALS IMEDIATELY
      return new Promise((resolve, reject) => {
        api
        .post('/auth/reset-password', {
          code: code,
          password: password,
          passwordConfirmation: passwordConfirmation
        }, {skipQueue: true}) //Skip upload queue
        .then(response => response.completionPromise)
        .then(() => {
          resolve();
        })
        .catch(error => {
          reject(error);
        });
      });
    },
    sendConfirmation () {
      return new Promise((resolve, reject) => {
        api
        .post('/auth/send-email-confirmation', undefined, {skipQueue: true}) //Skip upload queue
        .then(response => response.completionPromise)
        .then(() => {
          resolve();
        })
        .catch(error => {
          reject(error);
        });
      });
    },
    logout (context) {
      return new Promise((resolve, reject) => {
        context.dispatch('fetchUser') //TODO Call API to invalidate user token instead of getting user information? Or as a separate call to logout on all devices? Logged out on all devices when password changes? - For now detects blocked users
        .catch((error) => reject(error))
        .then(() => resolve())
        .finally(() => {
          context.dispatch('clearAllPersonalData', null, { root: true });
        });
      });
    },
    clearNonPrivateCaches () {
      //Clear all data in the public caches, once the service worker is available
      sendMessageToAllServiceWorkers({type: 'CLEAR_PUBLIC_CACHES'});
      api.clearCaches();
    },
    clearUserRoleRoutes (context) {
      //Clears all routes metadata that depend on user role to fetch them fully again
      context.commit('encyclopedia/forceReloadUserRoute', undefined, { root: true });
      context.commit('reports/forceReloadUserRoute', undefined, { root: true });
    },
    checkForUserUpdates (context, {user, previousUser}) {
      if (user != null) {
        if (previousUser != null) {
          //Compare if permissions are the same
          let permissionsChanged = !(_.isEqual(_.sortBy(previousUser.additional_permissions), _.sortBy(user.additional_permissions)));
          if (previousUser.role != user.role || permissionsChanged) { //If user role or permissions change, reset dependent indizes
            context.dispatch('clearUserRoleRoutes');
          }
        }

        if (user.legal_documents != null) {
          _.forEach(user.legal_documents, (legalDocument, documentType) => {
            if (!(legalDocument.isLatest)) {
              context.commit('addNotification', {
                title: `${documentType}_update_available`,
                description: 'view_legal_update',
                translate: true,
                action: 'legal_confirmation',
                payload: {...legalDocument, type: documentType},
                action_text: 'show',
                no_persist: true //Reload every time to clear potential old versions
              }, { root: true });
            } else {
              context.commit('removeMatchingNotification', {
                title: `${documentType}_update_available`,
                description: 'view_legal_update',
              }, { root: true });
            }
          });
        }
      }
    },
    fetchUser (context) {
      return new Promise((resolve, reject) => {
        api
        .get('/users/me')
        .then(response => {
          let previousUser = context.getters['getUser'];
          context.commit('setNewUser', response.data);
          context.dispatch('checkForUserUpdates', {user: response.data, previousUser});
          resolve();
        })
        .catch(error => {
          reject(error);
        });
      });
    },
    fetchLegalDocumentVersions () {
      return new Promise((resolve, reject) => {
        return api
          .get('/legal-documents')
          .then(async response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    },
    fetchLegalDocument (context, { type, locale, version }) { //TODO Allow caching, but with version or update!!!
      return new Promise((resolve, reject) => {
        return api
          .get(`/legal-documents/${type}`, {
            params: {
              version,
              locale
            }
          })
          .then(async response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    },
    updateLegalDocument (context, { type, version }) {
      return api.put('/users/legal-documents', {
        [type]: version
      }, {skipQueue: true})
      .then(response => response.completionPromise)
      .then(response => {
        context.dispatch('fetchUser');
        return response.data;
      });
    },
    requestRole (context, { name, birthname, requestedRole, proofFile }) {
      return new Promise((resolve, reject) => {
        (async () => (proofFile != null) ? convertFileToBlob(proofFile) : null)() //Only convert if it is set in async function
        .then(async (proofFileBlob) => await createFormData(
          (requestedRole != null) ? { 'name': name, 'birthname': birthname, 'requested_role': requestedRole } : {},
          (proofFileBlob != null) ? { 'role_proof': proofFileBlob } : {}
        ))
        .then((newRequest) => {
          if (newRequest == null) throw 'Invalid role request!';
          return api.put('/users/request-role', newRequest.formData, {skipQueue: true});
        })
        .then(response => response.completionPromise)
        .then(response => {
          context.commit('setNewUser', response.data);
          resolve();
        })
        .catch(error => {
          reject(error);
        });
      });
    },
    fetchUnverifiedUsers () {
      return api.get('/users/unverified').then((response) => response.data);
    },
    approveUserRole (context, userId) {
      if (userId == null) throw 'Invalid user ID';
      return api.put(`/users/approve-role/${userId}`, undefined, {skipQueue: true})
        .then(response => response.completionPromise)
        .then(response => response.data);
    },
    denyUserRole (context, userId) {
      if (userId == null) throw 'Invalid user ID';
      return api.put(`/users/deny-role/${userId}`, undefined, {skipQueue: true})
        .then(response => response.completionPromise)
        .then(response => response.data);
    },
    changeUserWithJWT (context, jwt) {
      return context.dispatch('clearAllPersonalData', null, { root: true })
      .then(() => {
        context.commit('setNewToken', jwt);
        return context.dispatch('fetchUser');
      }); 
    }
  }
}